import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
  AnimatePresence, motion, useScroll, useTransform,
} from 'framer-motion';
import { EASE, Reveal, MaskLine, Magnetic } from '../components/motion/primitives';
import Marquee from '../components/motion/Marquee';
import GazeFace from '../components/GazeFace';

const projects = [
  {
    to: '/propel',
    company: 'Propel Search',
    year: '2025',
    number: '01',
    title: 'From Job Search to Networking Platform',
    desc: 'Full product pivot - AI-powered LinkedIn extension. $1K MRR within months of launch.',
    tags: ['Product Strategy', '0 → 1', 'Chrome Extension'],
    thumb: '/propel-thumbnail-new.png',
  },
  {
    to: '/onboarding',
    company: 'Maqsad · Edtech',
    year: '2024',
    number: '02',
    title: 'When OTP Fails: Fixing Onboarding',
    desc: 'Redesigned login flow cut drop-offs by 22% and slashed OTP costs from PKR 200K → 40K/month.',
    tags: ['UX Design', 'Mobile', 'Edtech'],
    thumb: '/login-thumbnail-new.png',
  },
  {
    to: '/gamification',
    company: 'Maqsad · Edtech',
    year: '2024',
    number: '03',
    title: 'Maqsad Sikkay: Designing for Retention',
    desc: 'Virtual currency system - 2× app sessions, 25% resubscription via in-app payments.',
    tags: ['Gamification', 'Engagement', 'Monetisation'],
    thumb: '/gamification-thumbnail-new.png',
  },
  {
    to: '/the-brief',
    company: 'Personal Project',
    year: '2026',
    number: '04',
    title: 'The Brief: AI-Powered Case Study Writer',
    desc: 'Built my own tool that converts rough designer notes into portfolio case studies, interview prep, and recruiter-ready summaries.',
    tags: ['Product Thinking', '0→1', 'SaaS'],
    isPersonal: true,
    thumb: '/brief-thumbnail-new.png',
  },
];

const landingPages = [
  { company: 'Polaris Technologies', descriptor: 'Software Services', year: '2025', src: '/polaris.png' },
  { company: 'SJS', descriptor: 'Water Management SaaS', year: '2026', src: '/sjs.png' },
  { company: 'Propel Search', descriptor: 'Job SaaS', year: '2025', src: '/propel.png' },
  { company: 'Maqsad', descriptor: 'Edtech', year: '2024', src: '/maqsad.png' },
  { company: 'AIconomi', descriptor: 'Web3', year: '2024', src: '/aiconomi.png' },
  { company: 'Sparkloop', descriptor: 'Newsletter Growth', year: '2024', src: '/sparkloop.png' },
];

function useIsMobile() {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const fn = (e) => setMobile(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return mobile;
}

/* ── Scroll-driven word highlight ── */
function ManifestoWord({ progress, range, accent, children }) {
  const opacity = useTransform(progress, range, [0.14, 1]);
  return (
    <motion.span style={{ opacity }} className={accent ? 'mf-accent' : undefined}>
      {children}{' '}
    </motion.span>
  );
}

function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.82', 'end 0.45'] });
  const accentSet = new Set(['5+', 'years', '4', 'million', 'people.']);
  const words = 'A product designer with 5+ years turning messy problems into clean, impactful experiences. From 0→1 startups to products used by 4 million people.'.split(' ');
  return (
    <section className="manifesto">
      <Reveal y={14} blur={false} duration={0.6}>
        <p className="sec-label">( about )</p>
      </Reveal>
      <p className="mf-text" ref={ref}>
        {words.map((w, i) => (
          <ManifestoWord
            key={i}
            progress={scrollYProgress}
            range={[i / words.length, Math.min(1, (i + 1.5) / words.length)]}
            accent={accentSet.has(w)}
          >
            {w}
          </ManifestoWord>
        ))}
      </p>
    </section>
  );
}

/* ── Sticky stacking deck card ── */
const MEDIA_TINTS = [
  'linear-gradient(150deg, #FCE9DF 0%, #F6D0BA 100%)',
  'linear-gradient(150deg, #E7EEF8 0%, #D3E0F1 100%)',
  'linear-gradient(150deg, #ECE8FB 0%, #DCD6F4 100%)',
  'linear-gradient(150deg, #FFEEE4 0%, #FFD8C2 100%)',
];

function DeckCard({ p, i }) {
  return (
    <Reveal y={40} blur={false}>
      <Link to={p.to} className={`deck-row${i % 2 ? ' reverse' : ''}${p.isPersonal ? ' personal' : ''}`}>
        <div className="deck-media" style={{ background: MEDIA_TINTS[i % MEDIA_TINTS.length] }}>
          <img src={p.thumb} alt={p.title} loading="lazy" decoding="async" />
        </div>
        <div className="deck-body">
          <div className="deck-toprow">
            <span className="deck-company">
              {p.company}
              {p.year && <span className="deck-year">{p.year}</span>}
            </span>
            {p.isPersonal && <span className="deck-own">Own Product</span>}
          </div>
          <h2 className="deck-title">{p.title}</h2>
          <p className="deck-desc">{p.desc}</p>
          <div className="deck-foot">
            <div className="deck-tags">
              {p.tags.map(t => <span key={t} className="deck-tag">{t}</span>)}
            </div>
            <span className="deck-cta" aria-label="View case study">
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M7 17L17 7M17 7H7M17 7v10"/>
              </svg>
            </span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

/* ── Scattered parallax gallery (equal frames, straight, screen-filling) ── */
// cx/cy = centre position (% of the pinned viewport); w = width (vw);
// depth = parallax strength (foreground moves more); z = stack order.
// Staggered 3×2 offset grid: reads as scattered but everything stays axis-aligned.
const GAL_W = 31; // vw — one size for every frame
const galItems = [
  { ...landingPages[0], cx: 18, cy: 26, w: GAL_W, depth: 0.55, z: 3 },
  { ...landingPages[1], cx: 83, cy: 23, w: GAL_W, depth: 0.4, z: 2 },
  { ...landingPages[2], cx: 50, cy: 34, w: GAL_W, depth: 0.9, z: 5 },
  { ...landingPages[3], cx: 18, cy: 74, w: GAL_W, depth: 0.7, z: 4 },
  { ...landingPages[4], cx: 51, cy: 80, w: GAL_W, depth: 1.0, z: 6 },
  { ...landingPages[5], cx: 83, cy: 71, w: GAL_W, depth: 0.35, z: 2 },
];

function ParallaxGallery({ onOpen, sectionRef }) {
  const isMobile = useIsMobile();
  const pinRef = useRef(null);
  const stageRef = useRef(null);
  const tiltRef = useRef(null);
  const cardRefs = useRef([]);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] });
  // gentle zoom only — keeps the frames from pixelating
  const scale = useTransform(scrollYProgress, [0.1, 0.9], [1, 2.3]);
  // images + black backdrop fade out together, revealing the contact section
  // that underlaps directly behind the pin (no white flash in between)
  const opacity = useTransform(scrollYProgress, [0.5, 0.92], [1, 0]);

  useEffect(() => {
    if (isMobile) return;
    const pin = pinRef.current;
    const stage = stageRef.current;
    const tilt = tiltRef.current;
    if (!pin || !stage || !tilt) return;

    const clip = (v) => Math.max(-1, Math.min(1, v));
    const target = { x: 0, y: 0 };
    const cur = { x: 0, y: 0 };
    let raf = 0;
    let running = false;

    stage.style.transformOrigin = '50% 50%';

    const loop = () => {
      cur.x += (target.x - cur.x) * 0.08;
      cur.y += (target.y - cur.y) * 0.08;
      cardRefs.current.forEach((el, i) => {
        if (!el) return;
        const d = galItems[i].depth;
        // straight parallax — translation only, no rotation/tilt
        el.style.transform =
          `translate(-50%, -50%) translate(${(cur.x * d * 42).toFixed(1)}px, ${(cur.y * d * 42).toFixed(1)}px)`;
      });
      if (Math.abs(target.x - cur.x) < 0.0005 && Math.abs(target.y - cur.y) < 0.0005) {
        running = false;
        return;
      }
      raf = requestAnimationFrame(loop);
    };
    const kick = () => { if (!running) { running = true; raf = requestAnimationFrame(loop); } };

    const onMove = (e) => {
      const r = pin.getBoundingClientRect();
      target.x = -clip(((e.clientX - r.left) / r.width - 0.5) * 2);
      target.y = -clip(((e.clientY - r.top) / r.height - 0.5) * 2);
      // zoom origin follows the cursor, so scroll zooms into whatever it's over
      stage.style.transformOrigin = `${(e.clientX - r.left).toFixed(0)}px ${(e.clientY - r.top).toFixed(0)}px`;
      kick();
    };
    const onLeave = () => { target.x = 0; target.y = 0; kick(); };

    pin.addEventListener('pointermove', onMove, { passive: true });
    pin.addEventListener('pointerleave', onLeave);
    return () => {
      cancelAnimationFrame(raf);
      pin.removeEventListener('pointermove', onMove);
      pin.removeEventListener('pointerleave', onLeave);
    };
  }, [isMobile]);

  if (isMobile) {
    return (
      <section className="lp-section-mobile">
        <div className="sec-head">
          <p className="sec-label">( gallery )</p>
          <span className="sec-count">06 frames</span>
        </div>
        {landingPages.map((card, i) => (
          <Reveal key={card.company} y={30} blur={false} delay={0}>
            <button className="lpm-card" onClick={() => onOpen(i)}>
              <img src={card.src} alt={card.company} />
              <span className="lpm-caption">
                <span>{card.company}</span>
                <span className="lpm-meta">{card.descriptor} · {card.year}</span>
              </span>
            </button>
          </Reveal>
        ))}
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="gal">
      <div ref={pinRef} className="gal-pin">
        <motion.div className="gal-backdrop" style={{ opacity }} />
        <motion.div className="sec-head gal-head" style={{ opacity }}>
          <p className="sec-label">( gallery )</p>
        </motion.div>
        <motion.div ref={stageRef} className="gal-stage" style={{ scale, opacity }}>
          <div ref={tiltRef} className="gal-tilt">
            {galItems.map((it, i) => (
              <button
                key={it.company}
                ref={(el) => { cardRefs.current[i] = el; }}
                className="gal-card"
                style={{
                  left: `${it.cx}%`, top: `${it.cy}%`,
                  width: `${it.w}vw`, zIndex: it.z,
                  transform: 'translate(-50%, -50%)',
                }}
                onClick={() => onOpen(i)}
              >
                <img src={it.src} alt={it.company} loading="lazy" />
                <span className="gal-cap">{it.company}</span>
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SealBadge() {
  return (
    <div className="h2-badge" aria-hidden="true">
      <svg viewBox="0 0 120 120" className="h2-seal">
        <defs>
          <path id="h2-seal-path" d="M60,60 m-42,0 a42,42 0 1,1 84,0 a42,42 0 1,1 -84,0" />
        </defs>
        <text className="h2-seal-text">
          <textPath href="#h2-seal-path" startOffset="0">
            · PRODUCT DESIGNER · 5+ YEARS · SHIPPING AT SCALE&nbsp;
          </textPath>
        </text>
      </svg>
      <span className="h2-seal-star">✦</span>
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  // Scroll-linked hand-off: as the hero scrolls away it fades and zooms out,
  // so the next section arrives seamlessly instead of a hard cut.
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const opacity = useTransform(scrollYProgress, [0, 0.72], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.9]);
  const y = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section className="h2-hero" ref={ref}>
      <div className="h2-bg" aria-hidden="true">
        <div className="h2-bg-lines" />
      </div>

      <motion.div className="h2-inner" style={{ opacity, scale, y }}>
        <MaskLine delay={0.25}>
          <p className="h2-eyebrow">Hi, I&rsquo;m</p>
        </MaskLine>

        <h1 className="h2-name">
          <MaskLine delay={0.35}>
            <span className="h2-name-line">muneeb khan</span>
          </MaskLine>
        </h1>

        <motion.aside
          className="h2-bio"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9, ease: EASE }}
        >
          <p className="h2-bio-text">
            i <b>design and build</b> digital products, bridging product thinking,
            design, and engineering.
          </p>
        </motion.aside>

        <div className="h2-face">
          <motion.div
            className="h2-face-in"
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.6, ease: EASE }}
          >
            <GazeFace />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8, rotate: -20 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
          className="h2-badge-wrap"
        >
          <SealBadge />
        </motion.div>
      </motion.div>
    </section>
  );
}

export default function Home() {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Shared scroll progress for the gallery, so the contact section can blend
  // in (opacity 0→1) exactly as the gallery fades out — a true crossfade.
  const galRef = useRef(null);
  const isMobile = useIsMobile();
  const { scrollYProgress: galProgress } = useScroll({ target: galRef, offset: ['start start', 'end end'] });
  const contactOpacity = useTransform(galProgress, [0.55, 0.9], [0, 1]);

  return (
    <>
      <style>{`
        .sec-label {
          font-family: var(--mono); font-size: 11px; font-weight: 500;
          letter-spacing: 0.18em; text-transform: uppercase; color: var(--accent);
        }
        .sec-head {
          display: flex; justify-content: space-between; align-items: baseline;
          padding-bottom: 18px; margin-bottom: 0;
        }
        .sec-count {
          font-family: var(--mono); font-size: 11px; letter-spacing: 0.1em;
          text-transform: uppercase; color: var(--ink-faint);
        }

        /* -- HERO (big-name + gaze face) -- */
        .h2-hero {
          position: relative;
          min-height: 100vh;
          background: var(--paper);
          padding: clamp(104px, 13vh, 156px) clamp(20px, 4vw, 64px) 0;
          box-sizing: border-box;
          overflow: hidden;
        }
        .h2-inner {
          position: relative; z-index: 1;
          max-width: 1500px; margin: 0 auto;
          min-height: calc(100vh - clamp(104px, 13vh, 156px));
          display: flex; flex-direction: column; align-items: center;
          transform-origin: 50% 42%;
          will-change: transform, opacity;
        }

        /* moving-line background (subtle, drifts + one orange beam) */
        .h2-bg { position: absolute; inset: 0; z-index: 0; overflow: hidden; pointer-events: none; }
        .h2-bg-lines {
          position: absolute; inset: -2px;
          background-image: repeating-linear-gradient(
            90deg, rgba(14,14,12,0.045) 0 1px, transparent 1px clamp(64px, 8vw, 116px));
          animation: h2-drift 20s linear infinite;
        }
        @keyframes h2-drift { to { transform: translateX(clamp(64px, 8vw, 116px)); } }
        .h2-eyebrow {
          font-family: var(--sans); font-weight: 500;
          font-size: clamp(14px, 1.25vw, 19px);
          color: var(--ink-mid); text-align: center;
          margin-bottom: clamp(2px, 0.6vh, 8px);
        }
        .h2-name { margin: 0; z-index: 1; position: relative; text-align: center; }
        .h2-name-line {
          display: block;
          font-family: var(--sans); font-weight: 800;
          font-size: clamp(52px, 15.8vw, 236px);
          line-height: 0.82; letter-spacing: -0.05em;
          text-transform: lowercase; color: var(--ink);
          white-space: nowrap;
        }

        /* full portrait (head + neck) anchored at the bottom; a small nudge
           drops the canvas's transparent padding below the fold so the neck
           meets the marquee with no white gap, without cropping the neck */
        .h2-face {
          position: absolute; left: 50%; bottom: 0; z-index: 2;
          transform: translate(-50%, 4.5%);
          display: flex; justify-content: center;
        }
        .h2-face-in { display: flex; justify-content: center; }

        /* left column: short bio + current status */
        .h2-bio {
          position: absolute; left: 0; top: clamp(150px, 25vh, 280px);
          max-width: 320px; text-align: left; z-index: 3;
        }
        .h2-bio-text {
          font-family: var(--sans); font-weight: 400;
          font-size: clamp(13px, 1.02vw, 16px); line-height: 1.55;
          color: var(--ink-mid);
        }
        .h2-bio-text b { color: var(--accent); font-weight: 700; }

        /* right rotating seal */
        .h2-badge-wrap { position: absolute; right: 0; top: clamp(150px, 25vh, 280px); z-index: 3; }
        .h2-badge {
          position: relative;
          width: clamp(84px, 8vw, 118px); height: clamp(84px, 8vw, 118px);
          display: grid; place-items: center;
        }
        .h2-seal { width: 100%; height: 100%; animation: h2-spin 24s linear infinite; }
        .h2-seal-text {
          font-family: var(--mono); font-size: 8.4px; font-weight: 600;
          letter-spacing: 0.1em; fill: var(--ink);
        }
        .h2-seal-star {
          position: absolute; font-size: clamp(18px, 1.9vw, 26px); color: var(--accent);
        }
        @keyframes h2-spin { to { transform: rotate(360deg); } }

        /* the face - spec layer stack lives inside .gaze-stage */
        .gaze-stage {
          position: relative;
          height: clamp(340px, 58vh, 640px);
          aspect-ratio: 1134 / 1571;
          perspective: 1200px;
        }
        .gaze-head {
          position: absolute; inset: 0;
          transform-style: preserve-3d;
          transform-origin: 50% 100%; /* pivot at the base of the neck */
        }
        .gaze-layer {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          object-fit: contain;
          pointer-events: none; user-select: none;
        }
        .gaze-iris { overflow: visible; }
        .gaze-lid { opacity: 0; }

        /* ── ROLE MARQUEE ── */
        .role-marquee {
          background: var(--ink); color: var(--paper);
          padding: 18px 0; margin-top: 0;
        }
        .rmq-item {
          font-family: var(--poster); font-weight: 400;
          font-size: clamp(20px, 2.6vw, 34px); letter-spacing: 0.04em;
          text-transform: uppercase; padding: 0 26px; color: var(--paper);
        }
        .rmq-star { color: var(--accent); font-size: clamp(14px, 1.8vw, 22px); }

        /* ── MANIFESTO ── */
        .manifesto {
          max-width: 1150px; margin: 0 auto;
          padding: 20vh clamp(20px, 4vw, 56px) 16vh;
        }
        .manifesto .sec-label { display: block; margin-bottom: 40px; }
        .mf-text {
          font-family: var(--sans); font-weight: 600;
          font-size: clamp(30px, 3.9vw, 58px);
          line-height: 1.22; letter-spacing: -0.025em;
          color: var(--ink);
        }
        .mf-accent { color: var(--accent); }

        /* ── DECK ── */
        .deck-section { padding: 0 clamp(20px, 4vw, 56px); max-width: 1400px; margin: 0 auto; }
        .deck-list {
          display: flex; flex-direction: column;
          gap: clamp(48px, 7vw, 108px);
        }

        /* full-width alternating row: media | text */
        .deck-row {
          display: grid; grid-template-columns: 1.08fr 0.92fr;
          align-items: center; gap: clamp(32px, 5vw, 84px);
          text-decoration: none;
        }
        .deck-row.reverse .deck-media { order: 2; }
        .deck-row.reverse .deck-body { order: 1; }

        /* image mat — mockup fills the panel, very small radius */
        .deck-media {
          position: relative; width: 100%; aspect-ratio: 4 / 3;
          border-radius: 6px; overflow: hidden;
          display: flex; align-items: center; justify-content: center;
          transition: transform 0.6s var(--ease), box-shadow 0.6s var(--ease);
        }
        .deck-row:hover .deck-media {
          transform: translateY(-6px);
          box-shadow: 0 40px 70px -30px rgba(14,14,12,0.4);
        }
        .deck-media img {
          width: 100%; height: 100%; object-fit: cover; object-position: center;
          display: block; transition: transform 0.8s var(--ease);
        }
        .deck-row:hover .deck-media img { transform: scale(1.035); }

        .deck-body {
          display: flex; flex-direction: column; gap: 14px;
        }
        .deck-toprow {
          display: flex; align-items: center; gap: 14px;
          font-family: var(--mono); font-size: 10px; font-weight: 500; letter-spacing: 0.14em;
          text-transform: uppercase;
        }
        .deck-company { color: var(--ink-mid); display: inline-flex; align-items: baseline; gap: 10px; }
        .deck-year { color: var(--ink-faint); position: relative; padding-left: 12px; }
        .deck-year::before {
          content: ''; position: absolute; left: 0; top: 50%;
          width: 3px; height: 3px; border-radius: 50%;
          background: var(--accent); transform: translateY(-50%);
        }
        .deck-own {
          display: inline-flex; align-items: center; gap: 6px;
          font-family: var(--mono); font-size: 9px; font-weight: 600;
          letter-spacing: 0.12em; text-transform: uppercase; color: var(--accent);
          border: 1px solid rgba(255,77,0,0.4); border-radius: 100px; padding: 3px 9px;
        }
        .deck-own::before { content: ''; width: 5px; height: 5px; background: var(--accent); border-radius: 50%; }
        .deck-title {
          font-family: var(--sans); font-weight: 600;
          font-size: clamp(20px, 2vw, 32px);
          letter-spacing: -0.035em; line-height: 1.04;
          color: var(--ink); transition: color 0.25s; margin-top: 4px;
        }
        .deck-row:hover .deck-title { color: var(--accent); }
        .deck-desc {
          font-size: clamp(14px, 1.05vw, 16px); color: var(--ink-mid); line-height: 1.65; font-weight: 400;
          max-width: 46ch;
        }
        .deck-foot {
          margin-top: 10px;
          display: flex; align-items: center; justify-content: space-between; gap: 14px;
        }
        .deck-tags { display: flex; gap: 6px; flex-wrap: wrap; }
        .deck-tag {
          font-family: var(--mono); font-size: 9px; font-weight: 500;
          letter-spacing: 0.06em; text-transform: uppercase;
          padding: 5px 10px; border-radius: 100px;
          border: 1px solid var(--line); color: var(--ink-mid); white-space: nowrap;
        }
        .deck-cta {
          flex-shrink: 0; width: 42px; height: 42px; border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          border: 1px solid var(--line-strong); color: var(--ink);
          transition: background 0.3s var(--ease), border-color 0.3s, color 0.3s, transform 0.3s var(--ease);
        }
        .deck-cta svg { transition: transform 0.3s var(--ease); }
        .deck-row:hover .deck-cta {
          background: var(--accent); border-color: var(--accent); color: #fff;
          transform: scale(1.06);
        }
        .deck-row:hover .deck-cta svg { transform: translate(2px, -2px); }

        /* ── SCATTERED PARALLAX GALLERY ── */
        /* transparent section + fading black backdrop so the contact section
           (which underlaps behind the pin) is revealed as the images zoom out */
        .gal { position: relative; height: 220vh; margin-top: clamp(70px, 12vh, 150px); background: transparent; z-index: 1; }
        .gal-pin {
          position: sticky; top: 0; height: 100vh; overflow: hidden;
          background: transparent;
        }
        .gal-backdrop { position: absolute; inset: 0; z-index: 0; background: var(--ink); }
        .gal-head {
          position: absolute; top: 40px; left: clamp(20px, 4vw, 56px);
          right: clamp(20px, 4vw, 56px); z-index: 15; pointer-events: none;
        }
        .gal-stage { position: absolute; inset: 0; z-index: 1; will-change: transform, opacity; }
        .gal-tilt {
          position: absolute; inset: 0;
          transform-style: preserve-3d; will-change: transform;
        }
        .gal-card {
          position: absolute; padding: 0; border: none; background: none;
          cursor: pointer; border-radius: 10px; overflow: hidden;
          box-shadow: 0 30px 60px -22px rgba(0,0,0,0.7);
          will-change: transform;
          transition: box-shadow 0.35s var(--ease), filter 0.35s var(--ease);
        }
        .gal-card img {
          width: 100%; height: auto; aspect-ratio: 16 / 10;
          object-fit: cover; object-position: center top; display: block;
        }
        .gal-card::after {
          content: ''; position: absolute; inset: 0;
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.08); border-radius: 10px;
        }
        .gal-card:hover { box-shadow: 0 44px 90px -20px rgba(0,0,0,0.85); filter: brightness(1.08); }
        .gal-cap {
          position: absolute; left: 12px; bottom: 12px; z-index: 2;
          font-family: var(--mono); font-size: 10px; font-weight: 500;
          letter-spacing: 0.1em; text-transform: uppercase; color: #fff;
          background: rgba(14,14,12,0.72); backdrop-filter: blur(6px);
          padding: 6px 11px; border-radius: 100px;
          border: 1px solid rgba(255,255,255,0.14);
          opacity: 0; transform: translateY(6px); transition: opacity 0.3s var(--ease), transform 0.3s var(--ease);
        }
        .gal-card:hover .gal-cap { opacity: 1; transform: translateY(0); }

        /* mobile landing list */
        .lp-section-mobile { padding: 70px 24px; display: flex; flex-direction: column; gap: 28px; }
        .lpm-card { background: none; border: none; padding: 0; text-align: left; }
        .lpm-card img {
          width: 100%; aspect-ratio: 16/10; object-fit: cover; object-position: center top;
          display: block; border: 1px solid var(--line); border-radius: 10px;
        }
        .lpm-caption { display: flex; flex-direction: column; gap: 4px; padding-top: 12px; }
        .lpm-caption span:first-child { font-family: var(--sans); font-weight: 800; font-size: 16px; color: var(--ink); }
        .lpm-meta { font-family: var(--mono); font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--ink-faint); }

        /* lightbox */
        .lp-lightbox {
          position: fixed; inset: 0; background: rgba(14,14,12,0.92); z-index: 1000;
          display: flex; align-items: center; justify-content: center; padding: 32px;
        }
        .lp-lightbox-inner { max-width: min(1200px, 92vw); max-height: 84vh; overflow: hidden; border-radius: 8px; }
        .lp-lightbox-inner img { width: 100%; height: 100%; object-fit: contain; display: block; }
        .lp-lightbox-close {
          position: absolute; top: 22px; right: 22px; width: 44px; height: 44px;
          border-radius: 50%; background: var(--paper); border: none;
          color: var(--ink); display: flex; align-items: center; justify-content: center; cursor: pointer;
        }

        /* ── CONTACT ── */
        /* underlaps the last screen of the gallery pin (behind it, z-index 0),
           so as the gallery's black backdrop + images fade out the contact
           section is already sliding up in its place — no white flash between */
        .contact-wrap {
          position: relative; z-index: 0; background: var(--paper);
          margin-top: -100vh;
        }
        .contact-section {
          padding: 18vh clamp(20px, 4vw, 56px) clamp(28px, 4vh, 56px);
          max-width: 1600px; margin: 0 auto; box-sizing: border-box;
        }
        .contact-section .sec-label { display: block; margin-bottom: 44px; }
        .contact-heading {
          font-family: var(--poster); font-weight: 400;
          font-size: clamp(58px, 11vw, 190px);
          line-height: 0.88; letter-spacing: 0.005em;
          text-transform: uppercase; color: var(--ink);
          margin-bottom: 7vh;
        }
        .contact-heading .serif {
          font-family: var(--serif); font-style: italic; font-weight: 400;
          text-transform: none; color: var(--accent);
          font-size: 0.92em; letter-spacing: -0.01em;
        }
        .contact-email-row { margin-bottom: 8vh; }
        .contact-email {
          font-family: var(--sans); font-weight: 700;
          font-size: clamp(20px, 3.4vw, 46px); letter-spacing: -0.02em;
          color: var(--ink); text-decoration: none;
          position: relative; display: inline-block; padding-bottom: 6px;
        }
        .contact-email::after {
          content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 2px;
          background: var(--ink); transform-origin: right; transition: transform 0.4s var(--ease), background 0.3s;
        }
        .contact-email:hover { color: var(--accent); }
        .contact-email:hover::after { background: var(--accent); transform: scaleX(0); transform-origin: left; animation: none; }
        .contact-links-row {
          display: flex; justify-content: space-between; align-items: flex-end; gap: 32px;
          border-top: 1px solid var(--line); padding-top: 34px;
        }
        .contact-minor { display: flex; gap: 28px; }
        .contact-minor a {
          font-family: var(--mono); font-size: 12px; font-weight: 500;
          letter-spacing: 0.1em; text-transform: uppercase;
          color: var(--ink-mid); text-decoration: none; transition: color 0.2s;
          display: inline-flex; align-items: center; gap: 6px;
        }
        .contact-minor a:hover { color: var(--accent); }
        .contact-avail {
          font-family: var(--mono); font-size: 11px; letter-spacing: 0.08em;
          text-transform: uppercase; color: var(--ink-faint); text-align: right; line-height: 1.9;
        }

        @media (max-width: 900px) {
          /* Mobile: gallery is a normal list, so no pin to underlap */
          .contact-wrap { margin-top: 0; }
          /* Mobile: name → text → image pinned to the bottom of the screen */
          .h2-hero { padding: 88px 22px 0; min-height: 100vh; }
          .h2-inner { min-height: calc(100vh - 88px); }
          .h2-name-line { white-space: normal; font-size: clamp(44px, 15vw, 92px); line-height: 0.9; }
          .h2-eyebrow { order: 0; }
          .h2-name { order: 1; }
          .h2-bio { order: 2; }
          .h2-badge-wrap { order: 3; }
          .h2-face {
            order: 4; position: static; left: auto; bottom: auto;
            transform: none; margin-top: auto; align-self: center;
          }
          .gaze-stage { height: clamp(300px, 46vh, 460px); }
          .h2-bio, .h2-badge-wrap {
            position: static; top: auto; right: auto; left: auto;
            max-width: 360px; text-align: center; margin: 24px auto 0;
          }
          .h2-currently { align-items: center; }
          .h2-badge-wrap { display: flex; justify-content: center; margin-top: 26px; }
          .manifesto { padding: 90px 24px 70px; }
          .deck-section { padding: 0 16px; }
          .deck-list { gap: 56px; }
          .deck-row { grid-template-columns: 1fr; gap: 22px; }
          .deck-row.reverse .deck-media { order: 0; }
          .deck-row.reverse .deck-body { order: 0; }
          .deck-media { aspect-ratio: 16 / 11; }
          .deck-title { font-size: clamp(28px, 8vw, 40px); }
          .contact-section { padding: 90px 24px 60px; }
          .contact-links-row { flex-direction: column; align-items: flex-start; }
          .contact-avail { text-align: left; }
        }
      `}</style>

      {/* OPENING */}
      <Hero />

      {/* ROLE MARQUEE */}
      <Marquee className="role-marquee" duration={30}>
        {['Product Strategy', '0 → 1 Products', 'Design Systems', 'User Research', 'Fintech & SaaS', 'Interaction Design', 'Data-Informed Decisions', 'Shipping at Scale'].map((t) => (
          <span key={t} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <span className="rmq-item">{t}</span>
            <span className="rmq-star">✦</span>
          </span>
        ))}
      </Marquee>

      {/* MANIFESTO */}
      <Manifesto />

      {/* WORK DECK */}
      <section className="deck-section" id="work">
        <div className="sec-head">
          <p className="sec-label">( selected work )</p>
          <span className="sec-count">04 projects</span>
        </div>
        <Deck />
      </section>

      {/* GALLERY */}
      <ParallaxGallery onOpen={setLightboxIdx} sectionRef={galRef} />

      <AnimatePresence>
        {lightboxIdx !== null && (
          <motion.div
            className="lp-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxIdx(null)}
          >
            <motion.div
              className="lp-lightbox-inner"
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ type: 'spring', stiffness: 300, damping: 26 }}
            >
              <img src={landingPages[lightboxIdx].src} alt={landingPages[lightboxIdx].company} />
            </motion.div>
            <button className="lp-lightbox-close" onClick={() => setLightboxIdx(null)}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CONTACT — underlaps the gallery pin so it's revealed as the gallery fades */}
      <motion.div className="contact-wrap" style={isMobile ? undefined : { opacity: contactOpacity }}>
      <section className="contact-section" id="contact">
        <p className="sec-label">( get in touch )</p>
        <h2 className="contact-heading">
          <MaskLine delay={0.05}>Let's build</MaskLine>
          <MaskLine delay={0.15}>something</MaskLine>
          <MaskLine delay={0.25}><span className="serif">worth using.</span></MaskLine>
        </h2>
        <Reveal y={22} blur={false} delay={0.3}>
          <div className="contact-email-row">
            <Magnetic strength={0.12}>
              <a href="mailto:muneebkhan1999.mk@gmail.com" className="contact-email">
                muneebkhan1999.mk@gmail.com
              </a>
            </Magnetic>
          </div>
        </Reveal>
        <Reveal y={16} blur={false} delay={0.35}>
          <div className="contact-links-row">
            <div className="contact-minor">
              <a href="https://www.linkedin.com/in/muneeb-khan-10b3ba1b1/" target="_blank" rel="noreferrer">
                LinkedIn
                <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M7 17L17 7M17 7H7M17 7v10"/></svg>
              </a>
              <a href="/resume.pdf" target="_blank" rel="noopener noreferrer">
                Resume
                <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M7 17L17 7M17 7H7M17 7v10"/></svg>
              </a>
            </div>
            <div className="contact-avail">
              Open to opportunities<br />
              Remote &amp; Relocation
            </div>
          </div>
        </Reveal>
      </section>
      </motion.div>
    </>
  );
}

function Deck() {
  return (
    <div className="deck-list">
      {projects.map((p, i) => (
        <DeckCard key={p.to} p={p} i={i} />
      ))}
    </div>
  );
}
